import {
  AppShellContent,
  Button,
  Group,
  Headline,
  Skeleton,
  Stack,
  Text,
  Tile,
} from "@assetwatch/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { WorkPage } from "../components/layout/WorkPageLayout";
import { useRetainedLocalListState } from "../components/navigation/useRetainedListState";
import { ProcedureDetailPanel } from "../components/procedures/ProcedureDetailPanel";
import { ProcedureLibraryList } from "../components/procedures/ProcedureLibraryList";
import { ProcedureLoadError } from "../components/procedures/ProcedureLoadError";
import { ProcedureFilters } from "../components/snowflakes/procedures/ProcedureFilters";
import { SearchFilterToolbar } from "../components/snowflakes/SearchFilterToolbar";
import { useWorkScope } from "../components/WorkScopeProvider";
import { getWorkScopeSearch } from "../lib/workSearch";
import {
  adaptProcedureSummary,
  filterProcedureLibrary,
} from "../queries/procedureLibraryAdapter";
import { useProceduresQuery } from "../queries/useProceduresQuery";
import { useTeamsUsersQuery } from "../queries/useTeamsUsersQuery";

type ProcedureListState = {
  search: string;
  categories: string[];
  owners: string[];
};

const EMPTY_LIST_STATE: ProcedureListState = {
  search: "",
  categories: [],
  owners: [],
};

export function ProceduresScreen() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const navigate = useNavigate();
  const [{ search, categories, owners }, setListState] =
    useRetainedLocalListState<ProcedureListState>(
      "procedures",
      EMPTY_LIST_STATE,
    );
  const setSearch = (value: string) =>
    setListState((current) => ({ ...current, search: value }));
  const setCategories = (value: string[]) =>
    setListState((current) => ({ ...current, categories: value }));
  const setOwners = (value: string[]) =>
    setListState((current) => ({ ...current, owners: value }));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const hasAutoSelectedProcedure = useRef(false);
  const [collapsedGroups, setCollapsedGroups] = useState({
    all: false,
    deleted: true,
    mine: false,
  });
  const query = useProceduresQuery("");
  // Deleted Procedures the caller may restore. The server scopes this list, so
  // it is empty for anyone who cannot restore.
  const deletedQuery = useProceduresQuery("", "deleted");
  const teamsUsersQuery = useTeamsUsersQuery();
  const procedures = (query.data?.items ?? []).map(adaptProcedureSummary);
  const filtered = filterProcedureLibrary(
    procedures,
    search,
    categories,
    owners,
  );
  const deletedProcedures = filterProcedureLibrary(
    (deletedQuery.data?.items ?? []).map(adaptProcedureSummary),
    search,
    categories,
    owners,
  );
  const categoryValues =
    query.data?.facets.categories.status === "available"
      ? query.data.facets.categories.values
      : [];
  const ownerValues =
    query.data?.facets.owners.status === "available"
      ? query.data.facets.owners.values
      : [];
  const categoriesAvailable =
    query.data?.facets.categories.status === "available";
  const ownersAvailable = query.data?.facets.owners.status === "available";
  // A restored selection can name a value that no longer exists; once the
  // facets are known, keep only the values still on offer.
  useEffect(() => {
    const keptCategories = categories.filter((value) =>
      categoryValues.includes(value),
    );
    const keptOwners = owners.filter((value) => ownerValues.includes(value));
    const categoriesStale =
      categoriesAvailable && keptCategories.length !== categories.length;
    const ownersStale = ownersAvailable && keptOwners.length !== owners.length;
    if (!categoriesStale && !ownersStale) return;
    setListState((current) => ({
      ...current,
      categories: categoriesStale ? keptCategories : current.categories,
      owners: ownersStale ? keptOwners : current.owners,
    }));
  }, [
    categories,
    categoriesAvailable,
    categoryValues,
    owners,
    ownersAvailable,
    ownerValues,
    setListState,
  ]);
  const selectedActive =
    filtered.find((procedure) => procedure.id === selectedId) ?? null;
  const selectedDeleted = selectedActive
    ? null
    : (deletedProcedures.find((procedure) => procedure.id === selectedId) ??
      null);
  const selected = selectedActive ?? selectedDeleted;
  const selectedView = selectedDeleted ? "deleted" : "active";
  const currentUser = teamsUsersQuery.data?.users.find(
    (user) => user.id === teamsUsersQuery.data.currentUserId,
  );
  const currentUserTeams = currentUser?.teams ?? [];
  const myProcedures = filtered.filter(
    (procedure) =>
      (procedure.team !== null && currentUserTeams.includes(procedure.team)) ||
      procedure.owner === currentUser?.name,
  );
  const myProcedureIds = new Set(myProcedures.map((procedure) => procedure.id));
  const allProcedures = filtered.filter(
    (procedure) => !myProcedureIds.has(procedure.id),
  );
  const isRefreshing = query.isFetching || deletedQuery.isFetching;
  const firstVisibleProcedure =
    (!collapsedGroups.mine ? myProcedures[0] : undefined) ??
    (!collapsedGroups.all ? allProcedures[0] : undefined);
  useEffect(() => {
    if (
      selectedId !== null ||
      hasAutoSelectedProcedure.current ||
      query.isPending ||
      query.isError ||
      !teamsUsersQuery.data ||
      query.isFetching ||
      !firstVisibleProcedure
    )
      return;
    hasAutoSelectedProcedure.current = true;
    setSelectedId(firstVisibleProcedure.id);
  }, [
    firstVisibleProcedure,
    query.isError,
    query.isFetching,
    query.isPending,
    selectedId,
    teamsUsersQuery.data,
  ]);
  function selectProcedure(id: string) {
    hasAutoSelectedProcedure.current = true;
    setSelectedId(id);
  }
  useEffect(() => {
    // Wait for any refetch so a Procedure that just moved between the Library
    // and the Deleted group is not mistaken for a missing selection.
    if (isRefreshing || query.isPending || selectedId === null) return;
    // A selection that leaves the results is cleared rather than replaced, so
    // the user never sees another Procedure presented as the selection.
    if (
      !(
        filtered.some((procedure) => procedure.id === selectedId) ||
        deletedProcedures.some((procedure) => procedure.id === selectedId)
      )
    ) {
      setSelectedId(null);
    }
  }, [filtered, deletedProcedures, isRefreshing, query.isPending, selectedId]);
  function handoffToCreate() {
    if (!selectedCustomer || !selectedFacility) return;
    void navigate({
      search: {
        ...getWorkScopeSearch(selectedCustomer, selectedFacility),
        procedureId: undefined,
        aiDraftId: undefined,
      },
      // The AI Procedure Builder is hidden during the Beta, so every role
      // creates Procedures through manual authoring.
      to: "/procedures/authoring",
    });
  }

  return (
    <AppShellContent>
      <WorkPage
        header={
          <Group justify="between" wrap="wrap" gap={12}>
            <div>
              <Headline as="h1" size="lg">
                Procedures
              </Headline>
              <Text tone="subtle">Published procedures for this facility</Text>
            </div>
            <Group gap={8}>
              <Button icon="plus-regular" onClick={handoffToCreate} size="sm">
                Create procedure
              </Button>
            </Group>
          </Group>
        }
        toolbar={
          <SearchFilterToolbar
            onSearchChange={setSearch}
            searchAriaLabel="Search procedures by title or Procedure ID"
            searchPlaceholder="Search procedures"
            searchValue={search}
          >
            <ProcedureFilters
              categories={categoryValues}
              onCategoriesChange={setCategories}
              onOwnersChange={setOwners}
              onReset={() => {
                setCategories([]);
                setOwners([]);
              }}
              owners={ownerValues}
              selectedCategories={categories}
              selectedOwners={owners}
            />
          </SearchFilterToolbar>
        }
      >
        {query.isPending ? (
          <div className="grid min-h-[24rem] min-w-0 flex-1 grid-cols-1 gap-(--spacing-16) lg:min-h-0 lg:grid-rows-[minmax(0,1fr)] lg:grid-cols-[minmax(320px,420px)_minmax(0,1fr)]">
            <Tile hasBorder padding="none">
              <div className="p-(--spacing-16)">
                <Stack gap={12}>
                  {[1, 2, 3, 4].map((item) => (
                    <Skeleton height={72} key={item} width="100%" />
                  ))}
                </Stack>
              </div>
            </Tile>
            <Tile hasBorder padding="comfortable">
              <Skeleton height={32} width="50%" />
            </Tile>
          </div>
        ) : query.isError ? (
          <div className="flex min-h-[24rem] flex-1 flex-col lg:min-h-0 [&>*]:flex-1">
            <ProcedureLoadError
              appearance="tile"
              description="Retry, or contact your administrator if it keeps failing. Your search and filters are kept."
              isRetrying={query.isFetching}
              onRetry={() => void query.refetch()}
              title="Unable to load Procedures"
            />
          </div>
        ) : (
          <div className="grid min-h-[24rem] min-w-0 flex-1 grid-cols-1 gap-(--spacing-16) lg:min-h-0 lg:grid-rows-[minmax(0,1fr)] lg:grid-cols-[minmax(320px,420px)_minmax(0,1fr)]">
            <ProcedureLibraryList
              allProcedures={allProcedures}
              collapsedGroups={collapsedGroups}
              deletedProcedures={deletedProcedures}
              mineProcedures={myProcedures}
              onSelect={selectProcedure}
              onToggleGroup={(group) =>
                setCollapsedGroups((current) => ({
                  ...current,
                  [group]: !current[group],
                }))
              }
              selectedId={selected ? selectedId : null}
            />
            <div
              className={`${selected ? "flex" : "hidden lg:flex"} min-h-0 min-w-0 flex-col overflow-hidden rounded-(--radius-lg) border border-outline-subtle bg-surface shadow-elevation-1`}
              data-testid="procedures-detail-pane"
            >
              <ProcedureDetailPanel
                onBack={() => setSelectedId(null)}
                onDeleted={() => setSelectedId(null)}
                onEdit={() => {
                  if (!selected || !selectedCustomer || !selectedFacility)
                    return;
                  void navigate({
                    to: "/procedures/authoring",
                    search: {
                      ...getWorkScopeSearch(selectedCustomer, selectedFacility),
                      procedureId: selected.id,
                      aiDraftId: undefined,
                    },
                  });
                }}
                procedure={selected}
                defaultProcedureId={query.data?.defaultProcedureId ?? null}
                defaultProcedureName={
                  query.data?.defaultProcedure?.name ?? null
                }
                view={selectedView}
              />
            </div>
          </div>
        )}
      </WorkPage>
    </AppShellContent>
  );
}
